import * as yup from 'yup';
import { format } from 'date-fns';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { IAuthFormData } from '../model/types/AuthSchema';

export const authDataSchema: yup.ObjectSchema<IAuthFormData> = yup.object({
  email: yup.string().required('Enter login').email('Invalid email'),
  password: yup.string().required('Enter password'),
  remember: yup.boolean().default(false),
});

/**
 * Схема валидации пароля (с поддержкой латиницы и кириллицы)
 */
export const passwordSchema = (minLength: number = 8) =>
  yup
    .string()
    .required('Password is required')
    .min(minLength, `Minimum characters ${minLength}`)
    .matches(/[A-ZА-ЯЁ]/, 'One uppercase character')
    .matches(/[a-zа-яё]/, 'One lowercase character')
    .matches(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, 'One special character')
    .matches(/\d/, 'One number');

/**
 * Схема для формы установки/сброса пароля
 */
export const setPasswordFormSchema = (minLength: number = 8) =>
  yup.object({
    password: passwordSchema(minLength),
    confirmPassword: yup
      .string()
      .required('Please confirm your password')
      .test('passwords-match', 'Passwords do not match', function (value) {
        return this.parent.password === value;
      }),
  });

const EARLIEST_DATE_OF_BIRTH = '1900-01-01';

/**
 * Схема формы активации учётки: данные владельца и пароль.
 * Телефон — цифры из PhoneField без «+»; бэкенд принимает только действительный
 * международный номер. Дата рождения — YYYY-MM-DD, с 1900 года по вчера
 */
export const activateAccountFormSchema = (minLength: number = 8) =>
  yup.object({
    firstName: yup.string().trim().required('First name is required'),
    lastName: yup.string().trim().required('Last name is required'),
    phone: yup
      .string()
      .required('Phone number is required')
      .test(
        'valid-phone',
        'Invalid phone number',
        (value) => !value || Boolean(parsePhoneNumberFromString(`+${value}`)?.isValid()),
      ),
    dateOfBirth: yup
      .string()
      .required('Date of birth is required')
      .test('past-date', 'Date of birth must be in the past', (value) => {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
        const today = format(new Date(), 'yyyy-MM-dd');
        return value >= EARLIEST_DATE_OF_BIRTH && value < today;
      }),
    password: passwordSchema(minLength),
    confirmPassword: yup
      .string()
      .required('Please confirm your password')
      .test('passwords-match', 'Passwords do not match', function (value) {
        return this.parent.password === value;
      }),
  });

/**
 * Схема для формы изменения пароля
 */
export const changePasswordFormSchema = (minLength: number = 8) =>
  yup.object({
    currentPassword: yup.string().required('Current password is required'),
    password: passwordSchema(minLength),
    confirmPassword: yup
      .string()
      .required('Please confirm your password')
      .test('passwords-match', 'Passwords do not match', function (value) {
        return this.parent.password === value;
      }),
  });
